import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { combineLatest, Subject } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import {
    CreateOrUpdateBuyerImplementationPrimaryContactRequest,
    CreateOrUpdateBuyerImplementationPrimaryContactService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/create-or-update-buyer-implementation-primary-contact';
import {
    GetBuyerImplementationPrimaryContactRequest,
    GetBuyerImplementationPrimaryContactResponse,
    GetBuyerImplementationPrimaryContactService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-primary-contact';
import {
    CreateOrUpdateBuyerPrimaryContactRequest,
    CreateOrUpdateBuyerPrimaryContactService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/create-or-update-buyer-primary-contact';
import {
    GetBuyerPrimaryContactRequest,
    GetBuyerPrimaryContactResponse,
    GetBuyerPrimaryContactService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/get-buyer-primary-contact';
import { ManageBuyerService } from '../manage-buyer.service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@LifecycleAware()
@Component({
    selector: 'manage-buyer',
    styleUrls: ['./buyer-contact.component.scss'],
    template: `
        <div class="row">
            <div class="col-12 col-md-8">
                <card [headerTitle]="'Buyer Contact'" [style.marginBottom]="'3rem'">
                    <div card-body *ngIf="!initialized">
                        <lsq-circle-loader></lsq-circle-loader>
                    </div>
                    <div card-body class="contact-setup-card-body" *ngIf="showForm && initialized">
                        <p class="body" *ngIf="isImplementation" [style.marginBottom]="'2rem'">
                            The Primary Contact will receive an invite to create their FastTrack
                            account once buyer implementation is complete.
                        </p>
                        <p class="body" *ngIf="!isImplementation" [style.marginBottom]="'2rem'">
                            This is the Primary Contact for the Buyer. To invite additional users,
                            please use the
                            <a [routerLink]="['/']">Client Management Page</a>.
                        </p>
                        <form [formGroup]="contactFormGroup" (ngSubmit)="saveChanges()">
                            <div class="row">
                                <div class="col">
                                    <lsq-label label="First Name*"></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="primaryContactFirstName"
                                        formControlName="primaryContactFirstName"
                                        [group]="contactFormGroup"
                                    >
                                    </lsq-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col">
                                    <lsq-label label="Last Name*"></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="primaryContactLastName"
                                        formControlName="primaryContactLastName"
                                        [group]="contactFormGroup"
                                    >
                                    </lsq-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col">
                                    <lsq-label label="Email*"></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="primaryContactEmail"
                                        formControlName="primaryContactEmail"
                                        [group]="contactFormGroup"
                                    >
                                    </lsq-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 col-md-3" [style.marginTop]="'2rem'">
                                    <lsq-button
                                        appearance="secondary"
                                        (buttonClick)="resetForm()"
                                        [disabled]="isSubmitting"
                                        size="block"
                                    >
                                        Cancel
                                    </lsq-button>
                                </div>

                                <div class="col-12 col-md-3" [style.marginTop]="'2rem'">
                                    <lsq-button
                                        size="block"
                                        type="submit"
                                        appearance="primary"
                                        [disabled]="!(hasChanges$ | async)"
                                        [submitting]="isSubmitting"
                                    >
                                        Save
                                    </lsq-button>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div card-body class="contact-setup-card-body" *ngIf="!showForm">
                        <p class="body" *ngIf="isImplementation" [style.marginBottom]="'2rem'">
                            The Primary Contact will receive an invite to create their FastTrack
                            account once buyer implementation is complete.
                        </p>
                        <p class="body" *ngIf="!isImplementation" [style.marginBottom]="'2rem'">
                            This is the Primary Contact for the Buyer. To invite additional users,
                            please use the
                            <a [routerLink]="['/']">Client Management Page</a>.
                        </p>
                        <data-card id="contactSetupCard" [style.marginBottom]="'2rem'">
                            <p class="label">Primary Contact</p>
                            <div class="primary-contact">
                                <p class="body">{{ firstName }} {{ lastName }}</p>
                                <p class="body">
                                    {{ email }}
                                </p>
                                <div class="row">
                                    <div class="col-12">
                                        <lsq-button
                                            appearance="transparent"
                                            (buttonClick)="handlePrimaryContactClick()"
                                            [style.marginTop]="'1rem'"
                                        >
                                            Edit
                                        </lsq-button>
                                    </div>
                                </div>
                            </div>
                        </data-card>
                    </div>
                </card>
            </div>
            <div class="col-12 col-md-4">
                <data-card [lightBorder]="true">
                    <p class="header" [style.marginBottom]="'1rem'">Additional Information</p>
                    <p class="body text-dark-gray" [style.marginBottom]="'1rem'">
                        Please enter your updated information for the buyer and once you are done
                        click the Save button.
                    </p>
                </data-card>
            </div>
        </div>
    `
})
export class AdminManageBuyerContactComponent implements OnInit {
    public firstName: string;
    public lastName: string;
    public email: string;
    public isImplementation: boolean;
    public showForm = true;
    private _refetchSubject = new Subject();
    public initialized = false;
    public isSubmitting = false;
    private response: GetBuyerImplementationPrimaryContactResponse | GetBuyerPrimaryContactResponse;

    public contactFormGroup = new FormGroup({
        primaryContactFirstName: new FormControl({ value: null, disabled: false }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        primaryContactLastName: new FormControl({ value: null, disabled: false }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        primaryContactEmail: new FormControl({ value: null, disabled: false }, [
            Validators.required,
            LsqValidators.maxLength(255),
            LsqValidators.email
        ])
    });

    public hasChanges$ = this.contactFormGroup.valueChanges.pipe(
        map(
            (values) =>
                values.primaryContactFirstName !== this.response.primaryContactFirstName ||
                values.primaryContactLastName !== this.response.primaryContactLastName ||
                values.primaryContactEmail !== this.response.primaryContactEmail
        ),
        startWith(false),
        shareReplay(1)
    );

    constructor(
        private _manageBuyerService: ManageBuyerService,
        private _alertService: AlertService,
        private _getBuyerPrimaryContactService: GetBuyerPrimaryContactService,
        private _getBuyerImplementationPrimaryContactService: GetBuyerImplementationPrimaryContactService,
        private _updateBuyerPrimaryContatService: CreateOrUpdateBuyerPrimaryContactService,
        private _updateBuyerImplementationPrimaryContatService: CreateOrUpdateBuyerImplementationPrimaryContactService
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageBuyerService.isImplementation$,
            this._manageBuyerService.buyerKey$,
            this._refetchSubject.pipe(startWith(true))
        ])
            .pipe(forLifeOf(this))
            .subscribe(async ([isImplementation, buyerKey]) => {
                this.initialized = false;
                this.response = await this.loadBuyerPrimaryContactDetails(
                    isImplementation,
                    buyerKey
                );

                this.firstName = this.response.primaryContactFirstName;
                this.lastName = this.response.primaryContactLastName;
                this.email = this.response.primaryContactEmail;
                this.isImplementation = isImplementation;
                this.showForm = !this.firstName && !this.lastName && !this.email;

                this.contactFormGroup.patchValue({
                    primaryContactFirstName: this.firstName,
                    primaryContactLastName: this.lastName,
                    primaryContactEmail: this.email
                });

                this.initialized = true;
            });
    }

    public async handlePrimaryContactClick() {
        this.showForm = true;
    }

    public async loadBuyerPrimaryContactDetails(isImplementation: boolean, buyerKey: string) {
        if (isImplementation) {
            return this._getBuyerImplementationPrimaryContactService.executeOnce(
                new GetBuyerImplementationPrimaryContactRequest({
                    buyerImplementationKey: buyerKey
                })
            );
        } else {
            return this._getBuyerPrimaryContactService.executeOnce(
                new GetBuyerPrimaryContactRequest({
                    buyerKey: buyerKey
                })
            );
        }
    }

    public async saveChanges(): Promise<void> {
        this.contactFormGroup.markAllAsTouched();
        if (this.contactFormGroup.valid) {
            this.isSubmitting = true;
            const isImplementation = await this._manageBuyerService.isImplementation$
                .pipe(first())
                .toPromise();

            if (isImplementation) {
                await this._updateBuyerImplementationPrimaryContatService.executeOnce(
                    new CreateOrUpdateBuyerImplementationPrimaryContactRequest({
                        ...this.contactFormGroup.value,
                        buyerImplementationKey: await this._manageBuyerService.buyerKey$
                            .pipe(first())
                            .toPromise()
                    })
                );
            } else {
                await this._updateBuyerPrimaryContatService.executeOnce(
                    new CreateOrUpdateBuyerPrimaryContactRequest({
                        ...this.contactFormGroup.value,
                        buyerKey: await this._manageBuyerService.buyerKey$.pipe(first()).toPromise()
                    })
                );
            }
            this.isSubmitting = false;
            this._alertService.success('Buyer Primary Contact successfully updated.');
            this._refetchSubject.next();
        }
    }

    public resetForm(): void {
        this.contactFormGroup.reset({
            ...this.response
        });
        this.showForm = false;
    }

    ngOnDestroy(): void {}
}
